import { useEffect } from "react";
import { motion } from "framer-motion";
import { Download, ExternalLink } from "lucide-react";
import Seo from "@/components/Seo";

const EASE = [0.76, 0, 0.24, 1];

const ASSETS = [
  {
    name: "Primary Lockup — Horizontal",
    desc: "The full mark + wordmark treatment. Use on light backgrounds, in headers, offer letters, contracts.",
    file: "orphion-lockup-horizontal",
    ratio: "4.6 : 1",
    formats: [
      { label: "SVG", href: "/brand/orphion-lockup-horizontal.svg" },
      { label: "PNG · 1200", href: "/brand/orphion-lockup-horizontal-1200.png" },
      { label: "PNG · 2400 (print)", href: "/brand/orphion-lockup-horizontal-2400.png" },
    ],
    display: "bg-[var(--bg)]",
  },
  {
    name: "Stacked Lockup",
    desc: "Mark on top, wordmark centred below. Ideal for square avatars, share cards, presentation title slides.",
    file: "orphion-lockup-stacked",
    ratio: "1.16 : 1",
    formats: [
      { label: "SVG", href: "/brand/orphion-lockup-stacked.svg" },
      { label: "PNG · 720", href: "/brand/orphion-lockup-stacked-720.png" },
      { label: "PNG · 1440", href: "/brand/orphion-lockup-stacked-1440.png" },
    ],
    display: "bg-[var(--bg-2)]",
  },
  {
    name: "Pure Wordmark",
    desc: "Type-only lockup for editorial contexts — footer wordmark, marketing sign-offs, printed collateral.",
    file: "orphion-wordmark",
    ratio: "4.6 : 1",
    formats: [
      { label: "SVG", href: "/brand/orphion-wordmark.svg" },
      { label: "PNG · 1200", href: "/brand/orphion-wordmark-1200.png" },
      { label: "PNG · 2400 (print)", href: "/brand/orphion-wordmark-2400.png" },
    ],
    display: "bg-[var(--bg)]",
  },
  {
    name: "Monogram (Avatar)",
    desc: "512×512 badge — use as the app icon, LinkedIn logo, or profile avatar. Rounded corners built-in.",
    file: "orphion-monogram",
    ratio: "1 : 1",
    formats: [
      { label: "SVG", href: "/brand/orphion-monogram.svg" },
      { label: "PNG · 512", href: "/brand/orphion-monogram-512.png" },
      { label: "PNG · 1024", href: "/brand/orphion-monogram-1024.png" },
    ],
    display: "bg-[var(--bg-2)]",
    small: true,
  },
  {
    name: "Social Share Banner",
    desc: "1200×630 pre-composed banner — LinkedIn, X, WhatsApp, email header. Already used as the default Open Graph image.",
    file: "orphion-social",
    ratio: "1200 : 630",
    formats: [
      { label: "SVG", href: "/brand/orphion-social-1200x630.svg" },
      { label: "PNG · 1200×630", href: "/brand/orphion-social-1200.png" },
    ],
    display: "bg-[var(--bg)]",
  },
  {
    name: "Watermark Ghost",
    desc: "Ultra-light wordmark for full-bleed backgrounds — pitch decks, whitepapers, section headers.",
    file: "orphion-watermark",
    ratio: "10 : 3",
    formats: [
      { label: "SVG", href: "/brand/orphion-watermark.svg" },
      { label: "PNG · 2000", href: "/brand/orphion-watermark-2000.png" },
    ],
    display: "bg-[var(--bg)]",
  },
];

const COLLATERAL = [
  {
    name: "LinkedIn Company Banner",
    desc: "1584×396 cover image for the Orphion LinkedIn company page — lockup left, ghost star bleeding off the right edge.",
    src: "/brand/linkedin-banner-1584x396.png",
    ratio: "1584 : 396",
    dark: false,
  },
  {
    name: "LinkedIn Profile Logo",
    desc: "400×400 full-bleed avatar for the company page — circle-crop safe, no transparent corners.",
    src: "/brand/linkedin-logo-400x400.png",
    ratio: "400 : 400",
    dark: false,
    small: true,
  },
  {
    name: "Founder Business Card — Front",
    desc: "1050×600 (3.5×2 in @ 300 dpi) print-ready front — stacked lockup inside a hairline frame, five practices along the foot.",
    src: "/brand/business-card-front-1050x600.png",
    ratio: "1050 : 600",
    dark: false,
  },
  {
    name: "Founder Business Card — Back",
    desc: "Ink-dark reverse with founder name, title and contact lines. Bronze star + italic sign-off.",
    src: "/brand/business-card-back-1050x600.png",
    ratio: "1050 : 600",
    dark: true,
  },
];

const FAVICONS = [
  { size: "32", href: "/brand/favicon-32.png" },
  { size: "64", href: "/brand/favicon-64.png" },
  { size: "180 (Apple)", href: "/brand/favicon-180.png" },
  { size: "192 (Android)", href: "/brand/favicon-192.png" },
  { size: "512 (PWA)", href: "/brand/favicon-512.png" },
];

const COLORS = [
  { name: "Ivory", hex: "#f5f1e8", role: "Primary background", ink: false },
  { name: "Cream", hex: "#ede7d6", role: "Secondary surface", ink: false },
  { name: "Ink", hex: "#14110d", role: "Primary text", ink: true },
  { name: "Bronze", hex: "#b58033", role: "Accent · Italic HR", ink: true },
];

export default function BrandPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <Seo
        title="Brand assets — Orphion Global HR"
        description="Official Orphion Global HR brand assets — lockups, wordmarks, monogram, social banner and colour palette. Download SVG and PNG."
        path="/brand"
      />

      {/* Header */}
      <section className="pt-40 pb-16 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <p className="overline mb-6">Brand · Assets</p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.92] text-[var(--ink)]"
            >
              The{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                Orphion
              </span>{" "}
              brand kit.
            </motion.h1>
          </div>
          <div className="md:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
              className="text-[var(--ink-80)] text-lg leading-relaxed max-w-2xl"
            >
              Six official lockups, five favicon sizes and a four-colour palette
              — hand-drawn SVGs, exported to production-ready PNG. Use on
              contracts, offer letters, decks, social and every candidate
              touchpoint.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Assets */}
      <section className="py-20 md:py-24 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid gap-16">
            {ASSETS.map((a, i) => (
              <motion.div
                key={a.file}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, delay: 0.05 * i, ease: EASE }}
                className="grid md:grid-cols-12 gap-8 items-center border-t border-[var(--line-strong)] pt-10"
              >
                {/* Preview */}
                <div className={`md:col-span-8 ${a.display} border border-[var(--line-strong)] p-12 md:p-16 flex items-center justify-center`}>
                  <img
                    src={`/brand/${a.file}.svg`}
                    alt={a.name}
                    className={a.small ? "w-40 md:w-56" : "w-full max-w-[720px] h-auto"}
                    loading="lazy"
                  />
                </div>
                {/* Meta + downloads */}
                <div className="md:col-span-4">
                  <p className="overline mb-3">{a.ratio}</p>
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[var(--ink)] mb-3 leading-snug">
                    {a.name}
                  </h2>
                  <p className="text-[var(--ink-80)] text-sm leading-relaxed mb-6">
                    {a.desc}
                  </p>
                  <div className="flex flex-col gap-2">
                    {a.formats.map((f) => (
                      <a
                        key={f.href}
                        href={f.href}
                        download
                        className="flex items-center justify-between border border-[var(--line-strong)] hover:border-[var(--accent)] hover:text-[var(--accent)] px-4 py-3 transition-colors group text-sm"
                        data-testid={`brand-download-${a.file}-${f.label.split(" ")[0].toLowerCase()}`}
                      >
                        <span className="uppercase tracking-[0.22em] text-xs">{f.label}</span>
                        <Download className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100" />
                      </a>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ready-made collateral (LinkedIn + print) */}
      <section className="py-20 md:py-24 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <p className="overline mb-4">Collateral · Ready to upload</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[var(--ink)] mb-12 leading-tight">
            LinkedIn &{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              print
            </span>
            .
          </h2>
          <div className="grid gap-16">
            {COLLATERAL.map((c, i) => (
              <motion.div
                key={c.src}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.9, delay: 0.05 * i, ease: EASE }}
                className="grid md:grid-cols-12 gap-8 items-center border-t border-[var(--line-strong)] pt-10"
              >
                <div className="md:col-span-8 bg-[var(--bg-2)] border border-[var(--line-strong)] p-8 md:p-12 flex items-center justify-center">
                  <img
                    src={c.src}
                    alt={c.name}
                    className={c.small ? "w-40 md:w-56" : "w-full h-auto"}
                    loading="lazy"
                  />
                </div>
                <div className="md:col-span-4">
                  <p className="overline mb-3">{c.ratio}</p>
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight text-[var(--ink)] mb-3 leading-snug">
                    {c.name}
                  </h3>
                  <p className="text-[var(--ink-80)] text-sm leading-relaxed mb-6">
                    {c.desc}
                  </p>
                  <a
                    href={c.src}
                    download
                    className="flex items-center justify-between border border-[var(--line-strong)] hover:border-[var(--accent)] hover:text-[var(--accent)] px-4 py-3 transition-colors group text-sm"
                    data-testid={`brand-collateral-download-${i}`}
                  >
                    <span className="uppercase tracking-[0.22em] text-xs">PNG · Download</span>
                    <Download className="w-3.5 h-3.5 opacity-70 group-hover:opacity-100" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Favicons */}
      <section className="py-20 md:py-24 border-b border-[var(--line-strong)] bg-[var(--bg-2)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <p className="overline mb-4">Icons · Favicons</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[var(--ink)] mb-10 leading-tight">
            App icon &{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              favicon set
            </span>
            .
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {FAVICONS.map((f) => (
              <a
                key={f.href}
                href={f.href}
                download
                className="flex flex-col items-center gap-3 border border-[var(--line-strong)] p-6 bg-[var(--bg)] hover:border-[var(--accent)] transition-colors group"
              >
                <img
                  src={f.href}
                  alt={`Favicon ${f.size}`}
                  className="w-14 h-14"
                  loading="lazy"
                />
                <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--ink-70)] group-hover:text-[var(--accent)]">
                  {f.size}px
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Palette */}
      <section className="py-20 md:py-24 border-b border-[var(--line-strong)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <p className="overline mb-4">Palette</p>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[var(--ink)] mb-10 leading-tight">
            Four colours,{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              editorial
            </span>{" "}
            hierarchy.
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {COLORS.map((c) => (
              <div
                key={c.hex}
                className="border border-[var(--line-strong)] overflow-hidden group"
              >
                <div
                  className="h-40 flex items-end p-5"
                  style={{
                    background: c.hex,
                    color: c.ink ? "#f5f1e8" : "#14110d",
                  }}
                >
                  <p className="font-display text-2xl tracking-tight">{c.name}</p>
                </div>
                <div className="p-4 bg-[var(--bg-2)]">
                  <p className="font-mono text-sm text-[var(--ink)] mb-1">{c.hex}</p>
                  <p className="text-xs uppercase tracking-[0.22em] text-[var(--ink-70)]">
                    {c.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Usage rules */}
      <section className="py-20 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="overline mb-4">Guidance</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[var(--ink)] leading-tight">
              How to{" "}
              <span className="font-serif-accent italic text-[var(--accent)]">
                use it
              </span>
              .
            </h2>
          </div>
          <div className="md:col-span-8 space-y-6 text-[var(--ink-80)] leading-relaxed">
            <p>
              <strong className="text-[var(--ink)]">Do</strong> — use SVG whenever possible. Keep the ivory background (#f5f1e8) or a pure white surface. Preserve at least 40px of clear space around the mark on all sides. Bronze (#b58033) is reserved for the italic {`"global hr"`} accent and hairline underlines.
            </p>
            <p>
              <strong className="text-[var(--ink)]">Don&apos;t</strong> — don&apos;t recolour the italic accent to any tone other than bronze or ink. Don&apos;t place the wordmark on busy photography without the ivory panel. Don&apos;t stretch or condense the type. Don&apos;t use the diamond mark without the wordmark below print size 24pt.
            </p>
            <p>
              <strong className="text-[var(--ink)]">Attribution</strong> — footer or legal copy should read: <em>Orphion Global HR is a brand of Nivyan Business Solutions LLP, Bengaluru.</em>
            </p>
            <a
              href="https://orphionglobalhr.com"
              target="_blank"
              rel="noopener noreferrer"
              className="link-slide text-[var(--accent)] inline-flex items-center gap-1.5 pt-2"
            >
              <span>orphionglobalhr.com</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
